import React, { useState } from 'react'
import {
  Box,
  TextField,
  Grid,
  Typography,
  Button,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material'
import { CloudUpload, Delete, Preview } from '@mui/icons-material'
import { Controller, Control, FieldErrors } from 'react-hook-form'
import { parseFile, VehicleData, ParseResult } from '../../../../utils/fileParser'

export interface UploadMultipleVehiclesFormData {
  programId: string
  financier: string
  vehicles: VehicleData[]
}

interface UploadMultipleVehiclesFormProps {
  errors: FieldErrors<UploadMultipleVehiclesFormData>
  control: Control<UploadMultipleVehiclesFormData>
}

export const UploadMultipleVehiclesForm: React.FC<UploadMultipleVehiclesFormProps> = ({
  errors,
  control
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [parseResult, setParseResult] = useState<ParseResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showPreview, setShowPreview] = useState(false)

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, onChange: (value: VehicleData[]) => void) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsLoading(true)
    setUploadedFile(file)

    try {
      const result = await parseFile(file)
      setParseResult(result)

      // Update the form control with the parsed vehicles
      if (result.success) {
        onChange(result.vehicles)
      } else {
        onChange([])
      }
    } catch (error) {
      setParseResult({
        success: false,
        vehicles: [],
        count: 0,
        error: 'Failed to parse file'
      })
      console.log(error)
      onChange([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemoveFile = (onChange: (value: VehicleData[]) => void) => {
    setUploadedFile(null)
    setParseResult(null)
    setShowPreview(false)
    onChange([])

    // Reset file input
    const fileInput = document.getElementById('vehicle-file-upload') as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
  }

  const renderFileStatus = (onChange: (value: VehicleData[]) => void) => {
    if (!uploadedFile) return null

    return (
      <Paper sx={{ p: 2, mt: 2, backgroundColor: '#f5f5f5' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {uploadedFile.name}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {(uploadedFile.size / 1024).toFixed(1)} KB
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {parseResult && parseResult.success && (
              <>
                <Chip
                  label={`${parseResult.count} vehicles`}
                  color="success"
                  size="small"
                />
                {parseResult.count > 0 && (
                  <Tooltip title="Preview vehicles">
                    <IconButton
                      size="small"
                      onClick={() => setShowPreview(!showPreview)}
                    >
                      <Preview fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
            <Tooltip title="Remove file">
              <IconButton size="small" onClick={() => handleRemoveFile(onChange)}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {parseResult && !parseResult.success && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {parseResult.error}
          </Alert>
        )}

        {parseResult && parseResult.success && parseResult.count === 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            No valid vehicle data found in the file
          </Alert>
        )}
      </Paper>
    )
  }

  const renderVehiclePreview = () => {
    if (!showPreview || !parseResult || !parseResult.success || parseResult.vehicles.length === 0) {
      return null
    }

    return (
      <Paper sx={{ p: 2, mt: 2, maxHeight: 300, overflow: 'auto' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Vehicle Preview ({parseResult.count} vehicles)
        </Typography>
        <List dense>
          {parseResult.vehicles.slice(0, 10).map((vehicle, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemText
                primary={`${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim}`}
                secondary={`VIN: ${vehicle.vin} | Color: ${vehicle.exteriorColor} | Miles: ${vehicle.startingOdometerMiles}`}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          ))}
          {parseResult.vehicles.length > 10 && (
            <ListItem>
              <ListItemText
                primary={`... and ${parseResult.vehicles.length - 10} more vehicles`}
                primaryTypographyProps={{ variant: 'caption', fontStyle: 'italic' }}
              />
            </ListItem>
          )}
        </List>
      </Paper>
    )
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Upload Multiple Vehicles
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{xs:12, sm:6}}>
          <Controller
            name="programId"
            control={control}
            rules={{ required: 'Program ID is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Program ID"
                error={!!errors.programId}
                helperText={errors.programId?.message}
              />
            )}
          />
        </Grid>

        <Grid size={{xs:12, sm:6}}>
          <Controller
            name="financier"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Financier"
                error={!!errors.financier}
                helperText={errors.financier?.message}
              />
            )}
          />
        </Grid>

        <Grid size={{xs:12}}>
          <Typography variant="subtitle1" gutterBottom>
            Vehicle Data File
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Upload a CSV or JSON file containing vehicle information. The file should include columns for VIN, make, model, year, and other vehicle details.
          </Typography>

          <Controller
            name="vehicles"
            control={control}
            rules={{
              validate: (value) => {
                if (!value || value.length === 0) {
                  return 'Please upload a valid vehicle file'
                }
                return true
              }
            }}
            render={({ field: { onChange } }) => (
              <Box>
                <input
                  accept=".csv,.json"
                  style={{ display: 'none' }}
                  id="vehicle-file-upload"
                  type="file"
                  onChange={(e) => handleFileUpload(e, onChange)}
                  disabled={isLoading}
                />
                <label htmlFor="vehicle-file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUpload />}
                    disabled={isLoading}
                    fullWidth={!uploadedFile}
                  >
                    {isLoading ? 'Processing...' : uploadedFile ? 'Change File' : 'Upload Vehicle File'}
                  </Button>
                </label>

                {renderFileStatus(onChange)}
                {renderVehiclePreview()}
              </Box>
            )}
          />
        </Grid>

        <Grid size={{xs:12}}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>File Format Requirements:</strong>
            </Typography>
            <Typography variant="body2" component="div">
              <strong>CSV:</strong> Must include headers: vin, make, model, year (required). Optional: trim, exterior_color, interior_color, starting_odometer_miles, additional_details
            </Typography>
            <Typography variant="body2" component="div">
              <strong>JSON:</strong> Array of vehicle objects or single vehicle object with the same properties
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    </Box>
  )
}
